import { action, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { handleSaveFile } from '@/utils/file';
import { AxiosErrorApi, AxiosRequestApi, AxiosResponseApi } from '@/types/axios';
import ApiService from '@/apis/ApiService';
import { t } from 'i18next';
import { ResponseData } from '@/types/http';
import { getStorage } from '@/utils/browsers';
import { keyStorage } from '@/constant/string';
import { getIdToken } from '@/services/line';
import { httpStatus } from '@/configs/constants';

export interface CallApiOptions {
    handleErrorBySelf?: boolean,
    callbackError?: (response: AxiosResponseApi) => void,
    disableAlertError?: boolean,
    hideLoading?: boolean,
    isLoadingTable?: boolean
}
export default class ApiStore extends BaseStore {
    isLoading: boolean = false;
    isLoadingTable: boolean = false;
    requestCount: number = 0;
    requestCountTable: number = 0;
    refreshTokenPromise: Promise<any> | null = null;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            isLoading: observable,
            isLoadingTable: observable,
            showLoading: action.bound,
            hideLoading: action.bound,
            setLoading: action.bound,
            handlerGeneralResponseSuccess: action.bound,
            handlerGeneralResponseError: action.bound,
            call: action.bound
        });
    }

    showLoading() {
        this.isLoading = true;
    }

    hideLoading() {
        this.isLoading = false;
    }

    setLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    handlerGeneralResponseSuccess = (response: AxiosResponseApi): ResponseData => {
        if (response && response.status === 200) {
            const type = response.headers['content-type'];
            const isExport = [
                'application/pdf',
                'application/zip',
                'text/csv; charset=utf-8',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                ''
            ].includes(type);
            if (isExport) {
                handleSaveFile(response);
            }
            return response.data;
        }

        throw response;
    };

    handlerGeneralResponseError = (
        response?: AxiosResponseApi,
        { handleErrorBySelf, callbackError, disableAlertError }: Partial<CallApiOptions> = {}
    ): ResponseData => {
        if (handleErrorBySelf || !response) {
            throw response;
        }

        try {
            if (response.status >= httpStatus.INTERNAL_SERVER_ERROR) {
                this.rootStore.modalStore.showErrorModal({
                    content: response.data.message || t('messages.api_response_no_network_or_server_error')
                });
            } else if (response.status) {
                if (!disableAlertError) {
                    this.rootStore.modalStore.showErrorModal({
                        content: response.data.message || t('messages.api_response_no_message')
                    });
                }
                callbackError && callbackError(response);
            }
        } catch (error) {
            this.rootStore.modalStore.showErrorModal({
                content: t('messages.api_response_no_network_or_server_error')
            });
        } finally {
            throw response;
        }
    };

    call(
        contextRequest: ApiService,
        request: AxiosRequestApi,
        payload?: any,
        { handleErrorBySelf, callbackError, disableAlertError, hideLoading, isLoadingTable }: CallApiOptions = {}
    ): Promise<ResponseData> {
        this.requestCount += 1;
        if (!hideLoading) {
            this.showLoading();
        }
        if (isLoadingTable) {
            this.requestCountTable += 1;
            this.isLoadingTable = true;
        }
        return request.apply(contextRequest, [payload])
            .then(this.handlerGeneralResponseSuccess)
            .catch(async (response: AxiosErrorApi) => {
                const status = response?.status;
                if (status === httpStatus.FORBIDDEN) {
                    this.rootStore.modalStore.showErrorModal({ content: t('messages.account_invalid'), showCloseIcon: false });
                    setTimeout(() => {
                        this.rootStore.authStore.clearAuthentication();
                    }, 1500);
                } else if (status === httpStatus.TOKEN_INVALID) {
                    this.rootStore.modalStore.showErrorModal({ content: t('messages.token_invalid'), showCloseIcon: false });
                    setTimeout(() => {
                        this.rootStore.authStore.clearAuthentication();
                    }, 1500);
                } else if (status === httpStatus.TOKEN_EXPIRED) {
                    try {
                        if (!this.refreshTokenPromise) {
                            const liff_id = getStorage(keyStorage.LIFF_ID);
                            const user_uuid = getStorage(keyStorage.UUID);
                            const id_token = getIdToken();
                            const payload = { id_token, liff_id, user_uuid };

                            // Create promise for get new token
                            this.refreshTokenPromise = Promise.resolve(this.rootStore.authStore.getNewToken(payload));
                            await this.refreshTokenPromise;
                            this.refreshTokenPromise = null;
                        } else {
                            // have one api is waiting new token => wait it finish first
                            await this.refreshTokenPromise;
                        }
                        return this.call(
                            contextRequest,
                            request,
                            payload,
                            { handleErrorBySelf, callbackError, disableAlertError, hideLoading, isLoadingTable }
                        );
                    } catch (err) {
                        this.rootStore.modalStore.showErrorModal({
                            content: t('messages.token_expired_login_again')
                        });
                        this.rootStore.authStore.clearAuthentication();
                        throw err;
                    }
                }
                return this.handlerGeneralResponseError(response.response, { handleErrorBySelf, callbackError, disableAlertError });
            })
            .finally(() => {
                this.requestCount -= 1;
                if (this.requestCount === 0) {
                    this.hideLoading();
                }
                if (isLoadingTable) {
                    this.requestCountTable -= 1;
                    if (this.requestCountTable === 0) {
                        this.isLoadingTable = false;
                    }
                }
            });
    }
}
